import React, { useState, useEffect, useCallback } from 'react';
import DocPageHeader from 'components/docs/DocPageHeader';
import InvoicesManagementPanel from './InvoicesManagementPanel';
import { useLoading } from 'providers/LoadingProvider';
import useCustomer from 'hooks/useCustomer';
import { getDefaultInvoiceDateRange } from 'helpers/utils';

const InvoicesManagementPage: React.FC = () => {
  const [connectorGroupId, setConnectorGroupId] = useState<number>(() => {
    const params = new URLSearchParams(window.location.search);
    const connectorGroupIdParam = params.get('id');
    const parsedId = connectorGroupIdParam ? Number(connectorGroupIdParam) : 0;
    return parsedId;
  });

  const { showLoading, hideLoading } = useLoading();
  const [connectorGroupName, setConnectorGroupName] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('name') || 'Unknown Group';
  });

  // Get invoices from useCustomer hook
  const {
    invoices,
    fetchInvoices,
    fetchMoreInvoices,
    invoicesHasNextPage,
    invoicesLoading,
    invoicesLoadingMore,
    invoicesError,
    connectorGroups,
    connectors,
  } = useCustomer();
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
    getDefaultInvoiceDateRange
  );

  // نمایش loading در ابتدای لود صفحه
  useEffect(() => {
    showLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!connectorGroupId && connectorGroups && connectorGroups.length > 0) {
      const [firstConnectorGroup] = connectorGroups;
      if (firstConnectorGroup?.id) {
        setConnectorGroupId(firstConnectorGroup.id);
        setConnectorGroupName(firstConnectorGroup.display_name || firstConnectorGroup.name || connectorGroupName);
      }
    }
  }, [connectorGroupId, connectorGroups, connectorGroupName]);

  // Keep connectorGroup name in sync with selected connectorGroup
  useEffect(() => {
    if (!connectorGroupId || !connectorGroups || connectorGroups.length === 0) return;

    const match = connectorGroups.find(connectorGroup => connectorGroup.id === connectorGroupId);
    if (match) {
      const resolvedName = match.display_name || match.name || connectorGroupName;
      if (resolvedName !== connectorGroupName) {
        setConnectorGroupName(resolvedName);
      }
    }
  }, [connectorGroupId, connectorGroups, connectorGroupName]);

  // Fetch invoices when connectorGroupId changes (initial load)
  useEffect(() => {
    if (!connectorGroupId || connectorGroupId <= 0) {
      return;
    }

    fetchInvoices(dateRange.start, dateRange.end);
    // Intentionally omit dateRange: date changes go through handleDateRangeChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorGroupId, fetchInvoices]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      setDateRange({ start: startDate, end: endDate });
      fetchInvoices(startDate, endDate);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    return fetchMoreInvoices(dateRange.start, dateRange.end);
  }, [dateRange.start, dateRange.end, fetchMoreInvoices]);

  // نمایش loading به مدت حداقل 2000ms تا زمانی که داده‌ها لود شوند
  useEffect(() => {
    if (invoicesLoading) {
      showLoading();
    } else {
      // وقتی داده‌ها لود شدند، حداقل 2000ms صبر کن و بعد loading را مخفی کن
      const timeout = setTimeout(() => {
        hideLoading();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [invoicesLoading, showLoading, hideLoading]);

  return (
    <div>
      <DocPageHeader title="Explore" />
      <p>Browse and download the raw files collected from your connected carrier data sources.</p>
      <InvoicesManagementPanel
        resellerId={connectorGroupId}
        invoices={invoices}
        connectors={connectors}
        loading={invoicesLoading}
        error={invoicesError || undefined}
        onRefresh={() => {
          if (dateRange.start && dateRange.end) {
            fetchInvoices(dateRange.start, dateRange.end);
          }
        }}
        onLoadMore={handleLoadMore}
        hasNextPage={invoicesHasNextPage}
        loadingMore={invoicesLoadingMore}
        onDateRangeChange={handleDateRangeChange}
        dateRange={dateRange}
      />
    </div>
  );
};

export default InvoicesManagementPage;